// Tracks buffering stalls of the <video> element and decides when playback is bad enough
// to suggest another player. Pure logic, no React, so it is easy to unit-test.
//
// A stall is a `waiting` event that lasts at least `minStallMs` and does not follow a seek
// or a source change within `seekGraceMs`. `threshold` stalls inside `windowMs` trigger.

export const DEFAULT_STALL_OPTIONS = {
    windowMs: 90_000,
    threshold: 4,
    minStallMs: 1_000,
    seekGraceMs: 2_500,
};

export const createStallTracker = (options = {}) => {
    const config = { ...DEFAULT_STALL_OPTIONS, ...options };
    let stalls = [];
    let waitingSince = null;
    let lastSeekAt = -Infinity;

    const prune = (now) => {
        stalls = stalls.filter((time) => now - time <= config.windowMs);
    };

    return {
        /** User seeked or the source changed: the next stall is expected and ignored. */
        onSeek(now = Date.now()) {
            lastSeekAt = now;
            waitingSince = null;
        },
        onWaiting(now = Date.now()) {
            if (now - lastSeekAt <= config.seekGraceMs) return;
            if (waitingSince === null) waitingSince = now;
        },
        /** Playback resumed. Returns true when stalls are now frequent enough to suggest a switch. */
        onResume(now = Date.now()) {
            if (waitingSince === null) return false;
            const duration = now - waitingSince;
            waitingSince = null;
            if (duration < config.minStallMs) return false;
            stalls.push(now);
            prune(now);
            return stalls.length >= config.threshold;
        },
        stallCount(now = Date.now()) {
            prune(now);
            return stalls.length;
        },
        reset() {
            stalls = [];
            waitingSince = null;
            lastSeekAt = -Infinity;
        },
    };
};
